import { useState, useMemo, KeyboardEvent } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { useProjects } from '../hooks/useProjects';
import { BETA_FOLDERS_FEATURE_ENABLED } from '../config';
import { RouteMap } from '@/routes/Routes';
import { hasBetaProductName } from '@/utils/subscriptionHelpers';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { errorReporter } from '@/services/errorReportingService';

const MAX_FOLDERS = 5;

const ProjectsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userData } = useAuthStore();
  const { betaFeatures, isLoading: isPlanLoading } = useCurrentPlanFeatures();
  const isBetaTester = hasBetaProductName(userData) || !!betaFeatures.folders;
  const showAlert = useAlertStore((state) => state.showAlert);
  const [newFolderName, setNewFolderName] = useState('');

  const { projects, isLoading, isMutating, addProject, removeProject, updateProjectTags } = useProjects(
    isBetaTester && BETA_FOLDERS_FEATURE_ENABLED ? userData?.id : undefined,
  );

  // All hooks must be called before any early returns
  const sortedTags = useMemo(() => [...(userData?.tags ?? [])].sort((a, b) => a.localeCompare(b)), [userData?.tags]);
  const reachedLimit = projects.length >= MAX_FOLDERS;
  const descriptionText =
    'Create high-level buckets for your work. Assign existing tags to each folder and instantly pull up every note connected to those tags.';

  if (!BETA_FOLDERS_FEATURE_ENABLED) {
    return <Navigate to={RouteMap.HOME} replace />;
  }

  if (!hasBetaProductName(userData) && !betaFeatures.folders && !isPlanLoading) {
    return <Navigate to={RouteMap.BETA} replace />;
  }

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) {
      return;
    }

    if (projects.length >= MAX_FOLDERS) {
      showAlert(`You can only create up to ${MAX_FOLDERS} folders during the beta.`, 'info');
      return;
    }

    try {
      const result = await addProject(newFolderName);
      if (!result) {
        showAlert('That folder already exists or could not be created.', 'warning');
        return;
      }
      setNewFolderName('');
      showAlert('Folder created.', 'success');
    } catch (error) {
      errorReporter.expected('Failed to add project folder', error, {
        feature: 'projects',
        operation: 'addFolder',
      });
      showAlert('We could not create that folder. Please try again.', 'error');
    }
  };

  const handleKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await handleAddFolder();
    }
  };

  const handleDelete = async (folderId: string) => {
    try {
      await removeProject(folderId);
      showAlert('Folder deleted.', 'success');
    } catch (error) {
      errorReporter.expected('Failed to delete project folder', error, {
        feature: 'projects',
        operation: 'deleteFolder',
      });
      showAlert('We could not delete that folder. Please try again.', 'error');
    }
  };

  const handleTagsChange = async (folderId: string, tags: string[]) => {
    try {
      await updateProjectTags(folderId, tags);
      showAlert('Folder tags updated.', 'success');
    } catch (error) {
      errorReporter.expected('Failed to update project folder tags', error, {
        feature: 'projects',
        operation: 'updateFolderTags',
      });
      showAlert('We could not update those tags. Please try again.', 'error');
    }
  };

  return (
    <Box
      px={isMobile ? 2 : 4}
      py={4}
      sx={{
        maxWidth: '960px',
        margin: '0 auto',
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Folders (Beta)
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {descriptionText}
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6" fontWeight={600} display="flex" alignItems="center" gap={1}>
                <FolderOpenIcon fontSize="small" />
                Create a folder
              </Typography>
              <TextField
                label="Folder name"
                placeholder="Type a folder name and press Enter"
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
                disabled={isMutating || reachedLimit}
                helperText={
                  reachedLimit
                    ? `You've reached the ${MAX_FOLDERS} folder limit for the beta.`
                    : 'Organize related tags together so you can find notes faster.'
                }
              />
            </Stack>
          </CardContent>
        </Card>

        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" py={6}>
            <CircularProgress />
          </Stack>
        ) : projects.length === 0 ? (
          <Alert severity="info">
            Create your first folder to start grouping tags. Once you add tags to a folder, clicking it from the sidebar
            will filter your notes automatically.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid item xs={12} md={6} key={project.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                          {project.name}
                        </Typography>
                        <IconButton
                          aria-label={`Delete ${project.name}`}
                          onClick={() => handleDelete(project.id)}
                          size="small"
                          disabled={isMutating}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom display="flex" gap={1}>
                          <LabelOutlinedIcon fontSize="small" /> Assign tags
                        </Typography>
                        <Autocomplete
                          multiple
                          options={sortedTags}
                          value={project.tags}
                          onChange={(_, value) => handleTagsChange(project.id, value)}
                          disabled={isMutating}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={sortedTags.length > 0 ? 'Select tags' : 'No tags available'}
                              placeholder={sortedTags.length > 0 ? 'Add tags to this folder' : undefined}
                              helperText={
                                sortedTags.length === 0
                                  ? 'Add tags in Settings → Tags to start assigning them to folders.'
                                  : undefined
                              }
                            />
                          )}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    </Box>
  );
};

export default ProjectsPage;
